import React, { useEffect, useState, useRef } from "react";
import { topicDictionary } from "../Util/tileDictionary";
import * as d3 from "d3";
import * as analytics from "../Util/Analytics/TileAnalytics";
const DashboardTile = ({
  hideAllSubquestions,
  onVizTypeChange,
  question,
  setHideAllSubquestions,
  onSubtopicClick,
  location,
}) => {
  const selectRef = useRef(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  function showSubquestions() {
    if (!showSubquestion && selectRef.current) {
      // Check if dropdown would be cut off at the bottom
      const rect = selectRef.current.getBoundingClientRect();
      const dropdownHeight = 300; // max-height of dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If not enough space below but enough space above, open upward
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }
    }
    showSubquestion ? setShowSubquestion(false) : setShowSubquestion(true);
  }

  function hideSubquestions() {
    // hide the option container; don't reset the displayed label here
    // (we want to persist the user's selected label until the question changes)
    d3.selectAll(".option-container").attr("style", "visibility:hidden");
    setShowSubquestion(false);
    hideAllSubquestions ? setHideAllSubquestions(false) : setHideAllSubquestions(true);
  }
  useEffect(() => {
    d3.select("#App").on("click", hideSubquestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowSubquestion(false);
    setHideAllSubquestions(!hideAllSubquestions);
    // reset selection when the question changes
    setSelectedLabel("View All");
    setSubQuestionId(null);
    // It's okay to only reset on question change here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  function handleOnChange(value) {
    setSubQuestionId(value);
  }

  const [subQuestionId, setSubQuestionId] = useState(null);
  const [showSubquestion, setShowSubquestion] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("View All");

  function handleTableNavClick() {

    onVizTypeChange({
      vizType: "table",
      questionId: question.QuestionId,
      subQuestionId: subQuestionId,
      locationAbbr: null,
    });

    analytics.selectTable({ questionId: question.QuestionId });
  }
  function handleGraphNavClick() {

    onVizTypeChange({
      vizType: "graph",
      questionId: question.QuestionId,
      subQuestionId: subQuestionId,
      locationAbbr: null,
    });

    analytics.selectGraph({ questionId: question.QuestionId });
  }
  function handleMapNavClick() {

    // Map view only supports "All Locations", so always reset to ALL
    // Only show info banner when resetting from a non-ALL location (including Nationwide)
    const locationAbbr = location?.LocationAbbr;
    const isAllLocations = !locationAbbr || locationAbbr === "ALL";
    onVizTypeChange({
      vizType: "map",
      questionId: question.QuestionId,
      subQuestionId: subQuestionId,
      locationAbbr: "ALL",
      infoBannerMessage: isAllLocations ? null : 'Location has been reset to "All Locations" for map view.',
    });

    analytics.selectMap({ questionId: question.QuestionId });
  }
  const i = topicDictionary;
  const icon = i[question.TopicId]
    ? i[question.TopicId].icon
    : "topic-icon fs1 mx-1 cdc-icon-school-solid";

  function setOption(option) {
    analytics.selectSubQuestion({
      questionId: question.QuestionId,
      subQuestionId: option.SubQuestionId,
    });
    // Persist the selected label and id in React state so reopening the
    // dropdown shows the previously selected option instead of reverting
    // to "View All".
    const truncated = option.SubQuestion
      ? option.SubQuestion.substring(0, 13) + (option.SubQuestion.length > 13 ? "..." : "")
      : "";
    setSelectedLabel(truncated || "View All");
    setSubQuestionId(option.SubQuestionId);
    setShowSubquestion(false);
  }

  function handleSubtopicFilterClick(e) {
    e.stopPropagation();
    analytics.selectQCardSubTopic({
      topicId: question.TopicId,
      subTopicId: question.SubTopicId,
    });
    if (onSubtopicClick) {
      onSubtopicClick({
        topicId: question.TopicId,
        subTopicId: question.SubTopicId,
        subTopicDescription: question.SubTopicDescription,
      });
    }
  }
  return (
    <div
      aria-label="Question"
      className={"masonry-tile__wrapper yrbs-cat__color--" + question.TopicId}
      style={{
        backgroundColor: topicDictionary[question.TopicId].color,
      }}
    >
      <div
        onClick={handleTableNavClick}
        style={{ cursor: "pointer" }}
      >
        <div className="tile-header align-items-center row m-0 p-2">
          <div className="col-2 masonry-tile__icon p-0">
            <span className={"topic-icon " + icon}></span>
          </div>
          <div className="col-10 pl-2">
            <div className="topic-description">{question.TopicDescription}</div>
            <div
              className="subtopic-description"
              role="button"
              tabIndex="0"
              title="Filter by this subtopic"
              onClick={handleSubtopicFilterClick}
              onKeyDown={(e) => {
                if (e.code === "Enter" || e.key === "Enter" || e.keyCode === 13) {
                  handleSubtopicFilterClick(e);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {question.SubTopicDescription}
            </div>
          </div>
        </div>
        <p className="question-description p-2" style={{ cursor: "pointer" }}>
          {question.ShortQuestionDescription}
        </p>
      </div>
      <div className="masonry-tile__links pl-1 pr-1" style={{}}>
        {/* ---------------------------------------------- */}
        {question.SubQuestions && question.SubQuestions.length > 0 && (
          <>
            <p className="sub-question-text p-2">Select a Subquestion</p>
            <div className="pl-0">
              <div
                ref={selectRef}
                role="combobox"
                aria-label="select-subquestion"
                aria-expanded="false"
                aria-controls={`${question.QuestionId}-container`}
                tabIndex="0"
                className={`custom-select ${dropdownDirection === "up" ? "dropdown-up" : ""}`}
                onChange={handleOnChange}
                onClick={() => {
                  showSubquestions();
                }}
                onKeyDown={(e) => {
                  e.code === "Enter" && showSubquestions();
                }}
                defaultValue="ALL"
              >
                <div
                  className="option default"
                  value="ALL"
                  id={`${question.QuestionId}-default-value`}
                  aria-label="subquestion list"
                >
                  {selectedLabel}
                </div>
                <div
                  role="listbox"
                  className="option-container"
                  id={`${question.QuestionId}-container`}
                  style={
                    showSubquestion
                      ? { visibility: "visible" }
                      : { visibility: "hidden" }
                  }
                >
                  {/* Add View All as a selectable option so users can return to the default */}
                  <div
                    role="option"
                    aria-selected={subQuestionId === null}
                    tabIndex="0"
                    className="option"
                    id={`${question.QuestionId}-option0`}
                    value={"ALL"}
                    onClick={(e) => {
                      e.stopPropagation();
                      // reset to view all
                      setSelectedLabel("View All");
                      setSubQuestionId(null);
                      setShowSubquestion(false);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.code === "Enter" || e.key === "Enter" || e.keyCode === 13) {
                        setSelectedLabel("View All");
                        setSubQuestionId(null);
                        setShowSubquestion(false);
                      }
                    }}
                  >
                    View All
                  </div>
                  {question.SubQuestions &&
                    question.SubQuestions.map((option, index, key) => {
                      return (
                        <div
                          role="option"
                          aria-selected="false"
                          tabIndex="0"
                          className="option"
                          id={`${question.QuestionId}-option${index + 1}`}
                          key={option.SubQuestionId}
                          value={option.SubQuestionId}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOption(option);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            e.code === "Enter" && setOption(option);
                          }}
                        >
                          {option.SubQuestion}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </>
        )}
        {/* ------------------------------------- */}
        <div>
          <div className="row table-link-row">
            <div className="col-s-4 graph-link">
              <button
                tabIndex="0"
                className="btn btn-link"
                onClick={handleGraphNavClick}
              >
                <span className="cdc-icon-chart_01 mr-1"></span>
                Graphs
              </button>
            </div>
            <div className="col-s-4 table-link">
              <button
                tabIndex="0"
                className="btn btn-link "
                onClick={handleTableNavClick}
              >
                <span className="cdc-icon-table-light"></span> Table
              </button>
            </div>
            <div className="col-m-4 col-s-4 map-link">
              <button
                tabIndex="0"
                className="btn btn-link"
                onClick={handleMapNavClick}
              >
                <span className="cdc-icon-map_02"></span> Map
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTile;
